import type { MinimalCard } from '@/services/cardDatabase';

import type { AIPlayer, ColorCommitment } from '../types';

// --- Helpers ---

function isCreature(card: MinimalCard): boolean {
  return card.type_line?.toLowerCase().includes('creature') ?? false;
}

function getCardColors(card: MinimalCard): string[] {
  return card.color_identity;
}

// --- Scoring ---

function getBaseScore(card: MinimalCard): number {
  switch (card.rarity) {
    case 'mythic':
      return 5.0;
    case 'rare':
      return 4.0;
    case 'uncommon':
      return 3.0;
    case 'common':
      return 2.0;
    default:
      return 1.5;
  }
}

function getTypeBonus(card: MinimalCard): number {
  let bonus = 0;
  const typeLine = card.type_line?.toLowerCase() ?? '';

  // Instants and sorceries that could be removal
  if (typeLine.includes('instant') || typeLine.includes('sorcery')) {
    bonus += 0.8;
  }

  // Creatures are generally good in limited
  if (typeLine.includes('creature')) {
    bonus += 0.3;

    // Low-CMC creatures are better in limited
    const cmc = card.cmc ?? 0;
    if (cmc >= 2 && cmc <= 3) bonus += 0.3;
    if (cmc <= 1) bonus += 0.1;
  }

  // Planeswalkers are bombs
  if (typeLine.includes('planeswalker')) {
    bonus += 2.0;
  }

  return bonus;
}

function getColorBonus(card: MinimalCard, commitment: ColorCommitment, pickIndex: number): number {
  const cardColors = getCardColors(card);

  // Colorless cards are always playable
  if (cardColors.length === 0) return 0.5;

  // Phase 1: Speculation (picks 0-3)
  if (pickIndex < 4) {
    if (commitment.primary && cardColors.includes(commitment.primary)) return 0.3;
    if (commitment.secondary && cardColors.includes(commitment.secondary)) return 0.2;
    return 0;
  }

  // Phase 2: Commitment (picks 4-9)
  if (pickIndex < 10) {
    const isOnColor =
      (commitment.primary && cardColors.some((c) => c === commitment.primary)) ||
      (commitment.secondary && cardColors.some((c) => c === commitment.secondary));

    if (isOnColor) return 2.0;
    return -1.5;
  }

  // Phase 3: Filling (picks 10+)
  const isOnColor =
    (commitment.primary && cardColors.some((c) => c === commitment.primary)) ||
    (commitment.secondary && cardColors.some((c) => c === commitment.secondary));

  if (isOnColor) return 2.5;
  return -2.0;
}

function getCurveBonus(card: MinimalCard, pool: MinimalCard[], pickIndex: number): number {
  if (pickIndex < 6) return 0; // Too early to care about curve

  const cmc = card.cmc ?? 0;
  const poolCreatures = pool.filter(isCreature);
  const twosAndThrees = poolCreatures.filter((c) => (c.cmc ?? 0) >= 2 && (c.cmc ?? 0) <= 3);

  // If low on early creatures, prefer 2-3 drops
  if (twosAndThrees.length < 4 && cmc >= 2 && cmc <= 3 && isCreature(card)) {
    return 0.5;
  }

  // Penalize expensive cards if we already have many
  const expensive = pool.filter((c) => (c.cmc ?? 0) >= 5);
  if (expensive.length >= 4 && cmc >= 5) {
    return -0.5;
  }

  return 0;
}

/**
 * Score a card for AI picking.
 */
export function scoreCard(card: MinimalCard, commitment: ColorCommitment, pool: MinimalCard[], pickIndex: number): number {
  let score = getBaseScore(card);
  score += getTypeBonus(card);
  score += getColorBonus(card, commitment, pickIndex);
  score += getCurveBonus(card, pool, pickIndex);

  // Bomb override: always consider very high base-score cards
  if (getBaseScore(card) >= 4.5 && pickIndex < 10) {
    score = Math.max(score, getBaseScore(card) + getTypeBonus(card));
  }

  return score;
}

/**
 * Determine AI color commitment from pool.
 */
export function getAIColorCommitment(pool: MinimalCard[]): ColorCommitment {
  if (pool.length < 4) {
    return { primary: null, secondary: null, speculating: true };
  }

  // Count colors in pool
  const colorCounts: Record<string, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };
  for (const card of pool) {
    for (const color of card.color_identity) {
      colorCounts[color] = (colorCounts[color] || 0) + 1;
    }
  }

  // Sort by count
  const sorted = Object.entries(colorCounts)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  const primary = sorted[0]?.[0] ?? null;
  const secondary = sorted[1]?.[0] ?? null;
  const speculating = pool.length < 5;

  return { primary, secondary, speculating };
}

/**
 * Make an AI pick from available cards.
 */
export function makeAIPick(player: AIPlayer, availableCards: MinimalCard[], pickIndex: number, cardsPerPick: number): MinimalCard[] {
  if (availableCards.length === 0) return [];
  if (availableCards.length <= cardsPerPick) return [...availableCards];

  // Score all cards
  const scored = availableCards.map((card) => ({
    card,
    score: scoreCard(card, player.colorCommitment, player.pool, pickIndex),
  }));

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Pick top N
  return scored.slice(0, cardsPerPick).map((s) => s.card);
}

/**
 * Process a complete draft round: all AI players pick from their current packs,
 * then packs rotate.
 *
 * Returns updated packs after rotation.
 */
export function processAIPicks(
  aiPlayers: AIPlayer[],
  currentPacks: MinimalCard[][],
  aiPackIndices: number[],
  pickIndex: number,
  cardsPerPick: number,
): { updatedPlayers: AIPlayer[]; updatedPacks: MinimalCard[][] } {
  const updatedPlayers = aiPlayers.map((player, i) => {
    const packIdx = aiPackIndices[i];
    const pack = currentPacks[packIdx];
    if (!pack || pack.length === 0) return player;

    const picked = makeAIPick(player, pack, pickIndex, cardsPerPick);
    const newPool = [...player.pool, ...picked];
    const newCommitment = getAIColorCommitment(newPool);

    return {
      ...player,
      pool: newPool,
      colorCommitment: newCommitment,
    };
  });

  // Remove picked cards from packs
  const updatedPacks = currentPacks.map((pack, packIdx) => {
    const playerForPack = aiPackIndices.findIndex((idx) => idx === packIdx);
    if (playerForPack === -1) return pack; // This is the player's pack

    const player = updatedPlayers[playerForPack];
    const lastPicked = player.pool.slice(-cardsPerPick);
    const pickedIds = new Set(lastPicked.map((c) => c.id));
    return pack.filter((c) => !pickedIds.has(c.id));
  });

  return { updatedPlayers, updatedPacks };
}
